import { useMemo } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Loader2, Sparkles } from 'lucide-react';

import { CatalogGrid } from '@/components/catalog/CatalogGrid';
import { CartSidebar } from '@/components/cart/CartSidebar';
import { Button } from '@/components/ui/button';
import { useCatalog } from '@/hooks/useCatalog';
import { useCustomerPricing } from '@/hooks/useCustomerPricing';
import { useCart } from '@/hooks/useCart';
import { useOrderHistory, type ItemFrequency } from '@/hooks/useOrderHistory';
import { isPickupItem, type CatalogItem, type Category } from '@/types/domain';
import type { ShellOutletCtx } from '@/lib/shell-context';

/**
 * /collection — the customer's personalized catalog view.
 *
 * Top section: "Your regulars" — items they've ordered before, ordered by
 * how often (descending). When there's no order history yet, this section
 * hides and a friendly nudge points them at the full catalog.
 *
 * Below: "Browse the rest" — items they haven't ordered, grouped by
 * category. Each category gets a heading + grid.
 *
 * Same right-rail cart sidebar as /order so adding an item works the same
 * way.
 */
export default function MyCollection() {
  const navigate = useNavigate();
  const { selectedLocation } = useOutletContext<ShellOutletCtx>();
  const { data: allItems = [], isLoading: catalogLoading } = useCatalog();
  // Pickup/return rows are never browsable products (see Cart's tank-return flow).
  const items = useMemo(() => allItems.filter((i) => !isPickupItem(i)), [allItems]);
  const { data: pricing } = useCustomerPricing();
  const { data: history, isLoading: historyLoading } = useOrderHistory();
  const { cart, setQty, adjustQty, remove } = useCart();

  const { regulars, restByCategory, totalOrders, hasRegulars } = useMemo(() => {
    const freq: Map<string, ItemFrequency> = history?.byCatalogItemId ?? new Map();
    const total = history?.totalOrders ?? 0;

    const reg: CatalogItem[] = items
      .filter((i) => freq.has(i.id))
      .sort((a, b) => {
        const fa = freq.get(a.id);
        const fb = freq.get(b.id);
        return (fb?.orderCount ?? 0) - (fa?.orderCount ?? 0);
      });

    const regIds = new Set(reg.map((i) => i.id));
    const rest: Record<Category, CatalogItem[]> = {
      '3-gal BIB': [],
      '5-gal BIB': [],
      'Sugar Free': [],
      Mixer: [],
      '24-Pack': [],
      Gas: [],
      Other: [],
    };
    for (const it of items) {
      if (regIds.has(it.id)) continue;
      const cat = (rest[it.category] ?? rest.Other) as CatalogItem[];
      cat.push(it);
    }

    return {
      regulars: reg,
      restByCategory: rest,
      totalOrders: total,
      hasRegulars: reg.length > 0,
    };
  }, [items, history]);

  const loading = catalogLoading || historyLoading;

  // Category render order for the "Browse the rest" section.
  const categoryOrder: Category[] = [
    '3-gal BIB',
    'Sugar Free',
    'Mixer',
    '5-gal BIB',
    '24-Pack',
    'Gas',
    'Other',
  ];

  return (
    <div className="flex h-full">
      <section className="flex-1 min-w-0 overflow-y-auto scrollbar-thin pb-20 md:pb-10">
        <div className="px-6 md:px-8 pt-6">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-1">
            <div>
              <h1 className="font-display text-3xl font-semibold tracking-tight text-brix-ink">
                My Collection
              </h1>
              <p className="text-sm mt-0.5 text-brix-muted">
                {hasRegulars
                  ? `Your regulars first · ${totalOrders} ${totalOrders === 1 ? 'past order' : 'past orders'}`
                  : 'Items you order most often will live here once you start placing orders.'}
              </p>
            </div>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => navigate('/order')}
            >
              Browse full catalog
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 flex flex-col items-center gap-2">
            <Loader2 size={20} className="animate-spin text-brix-primary" />
            <p className="text-sm text-brix-muted">Loading your collection…</p>
          </div>
        ) : (
          <div className="px-6 md:px-8 py-6 space-y-10">
            {hasRegulars && (
              <section aria-labelledby="regulars-heading">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles size={16} className="text-brix-accent" />
                  <h2
                    id="regulars-heading"
                    className="font-display text-xl font-semibold text-brix-ink"
                  >
                    Your regulars
                  </h2>
                  <span className="text-xs font-mono text-brix-muted">
                    {regulars.length}
                  </span>
                </div>
                <CatalogGrid
                  items={regulars}
                  pricing={pricing}
                  cart={cart}
                  onAdjust={adjustQty}
                  onSet={setQty}
                />
              </section>
            )}

            <section aria-labelledby="rest-heading">
              <h2
                id="rest-heading"
                className="font-display text-xl font-semibold text-brix-ink mb-1"
              >
                {hasRegulars ? 'Browse the rest' : 'Browse everything'}
              </h2>
              <p className="text-xs text-brix-muted mb-4">
                Grouped by category.
              </p>

              <div className="space-y-8">
                {categoryOrder.map((cat) => {
                  const list = restByCategory[cat];
                  if (!list || list.length === 0) return null;
                  return (
                    <div key={cat}>
                      <div className="flex items-center gap-2 mb-3">
                        <h3 className="font-display text-base font-semibold text-brix-ink">
                          {cat}
                        </h3>
                        <span className="text-xs font-mono text-brix-muted">
                          {list.length}
                        </span>
                      </div>
                      <CatalogGrid
                        items={list}
                        pricing={pricing}
                        cart={cart}
                        onAdjust={adjustQty}
                        onSet={setQty}
                      />
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}
      </section>

      <CartSidebar
        items={items}
        pricing={pricing}
        cart={cart}
        locationName={selectedLocation}
        onAdjust={adjustQty}
        onSet={setQty}
        onRemove={remove}
        onSubmit={() => navigate('/cart')}
      />
    </div>
  );
}
